import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, BookOpen, FileText, ExternalLink, Download, Upload, RefreshCw, Globe, Check, AlertCircle, Clock, Eye, Loader2 } from 'lucide-react';
import { api, Course, Lesson, ScrapedContent, ScrapeJobStatus } from '../lib/api';
import ClickFunnelsCourseImportModal from './ClickFunnelsCourseImportModal';
import CSVImportModal from './CSVImportModal';

export default function CourseManager() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Record<string, Lesson[]>>({});
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // New course form
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [newCourseDesc, setNewCourseDesc] = useState('');
  const [creatingCourse, setCreatingCourse] = useState(false);

  // New lesson form
  const [showLessonForm, setShowLessonForm] = useState<string | null>(null);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonDesc, setNewLessonDesc] = useState('');
  const [newLessonUrl, setNewLessonUrl] = useState('');
  const [creatingLesson, setCreatingLesson] = useState(false);

  // Import modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);

  // Scraping / sync state
  const [scrapingLesson, setScrapingLesson] = useState<string | null>(null);
  const [scrapingCourse, setScrapingCourse] = useState<string | null>(null);
  const [syncingCourse, setSyncingCourse] = useState<string | null>(null);

  // Queue-based scrape job tracking
  const [scrapeJobId, setScrapeJobId] = useState<string | null>(null);
  const [scrapeJobStatus, setScrapeJobStatus] = useState<ScrapeJobStatus | null>(null);
  const selectedLessonIdRef = useRef<string | null>(null);

  // Lesson detail view
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [lessonDetail, setLessonDetail] = useState<ScrapedContent | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<'text' | 'markdown' | 'html'>('text');

  useEffect(() => {
    loadCourses();
  }, []);

  // Keep ref in sync for use inside polling closure
  useEffect(() => { selectedLessonIdRef.current = selectedLessonId; }, [selectedLessonId]);

  // Poll scrape job status
  useEffect(() => {
    if (!scrapeJobId) return;

    let cancelled = false;
    let done = false;

    const poll = async () => {
      if (done || cancelled) return;
      try {
        const status = await api.getScrapeJobStatus(scrapeJobId);
        if (cancelled) return;
        setScrapeJobStatus(status);

        if (status.status === 'completed') {
          done = true;
          setScrapeJobId(null);
          setScrapingCourse(null);
          showSuccess(
            `Scraped ${status.succeeded}/${status.total}${status.failed > 0 ? ` (${status.failed} failed)` : ''}`
          );
          // Refresh lesson list
          const data = await api.getLessons(status.courseId);
          if (!cancelled) setLessons(prev => ({ ...prev, [status.courseId]: data }));
          // Refresh detail panel if open
          const currentLessonId = selectedLessonIdRef.current;
          if (currentLessonId) {
            try {
              const content = await api.getScrapedContent(currentLessonId);
              if (!cancelled) setLessonDetail(content);
            } catch {}
          }
          if (!cancelled) setScrapeJobStatus(null);
        }
      } catch {
        // Ignore polling errors
      }
    };

    poll();
    const id = setInterval(poll, 2000);

    return () => { cancelled = true; clearInterval(id); };
  }, [scrapeJobId]);

  const loadCourses = async () => {
    try {
      const data = await api.getCourses();
      setCourses(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async (courseId: string) => {
    if (lessons[courseId]) return;

    try {
      const data = await api.getLessons(courseId);
      setLessons({ ...lessons, [courseId]: data });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const toggleCourse = (courseId: string) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
    } else {
      setExpandedCourse(courseId);
      loadLessons(courseId);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourseTitle.trim()) return;

    setCreatingCourse(true);
    setError(null);

    try {
      const course = await api.createCourse(newCourseTitle.trim(), newCourseDesc.trim() || undefined);
      setCourses([course, ...courses]);
      setNewCourseTitle('');
      setNewCourseDesc('');
      setShowCourseForm(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleDeleteCourse = async (id: string) => {
    if (!confirm('Are you sure you want to delete this course and all its lessons?')) return;

    try {
      await api.deleteCourse(id);
      setCourses(courses.filter(c => c.id !== id));
      const newLessons = { ...lessons };
      delete newLessons[id];
      setLessons(newLessons);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateLesson = async (e: React.FormEvent, courseId: string) => {
    e.preventDefault();
    if (!newLessonTitle.trim() || !newLessonUrl.trim()) return;

    setCreatingLesson(true);
    setError(null);

    try {
      const lesson = await api.createLesson({
        course_id: courseId,
        title: newLessonTitle.trim(),
        description: newLessonDesc.trim() || undefined,
        url: newLessonUrl.trim(),
      });
      setLessons({
        ...lessons,
        [courseId]: [...(lessons[courseId] || []), lesson],
      });
      setNewLessonTitle('');
      setNewLessonDesc('');
      setNewLessonUrl('');
      setShowLessonForm(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingLesson(false);
    }
  };

  const handleDeleteLesson = async (lessonId: string, courseId: string) => {
    if (!confirm('Are you sure you want to delete this lesson?')) return;

    try {
      await api.deleteLesson(lessonId);
      setLessons({
        ...lessons,
        [courseId]: lessons[courseId].filter(l => l.id !== lessonId),
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ============ Scraping & Sync Handlers ============

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  const handleScrapeLesson = async (lessonId: string, courseId: string) => {
    setScrapingLesson(lessonId);
    setError(null);
    try {
      const result = await api.scrapeLessonContent(lessonId);
      showSuccess(`Scraped "${result.title}" — ${result.wordCount} words, ${result.videosDetected} videos detected`);
      // Refresh lessons to show updated content status
      const data = await api.getLessons(courseId);
      setLessons(prev => ({ ...prev, [courseId]: data }));
      // Refresh detail panel if this lesson is currently being viewed
      if (selectedLessonId === lessonId) {
        try {
          const content = await api.getScrapedContent(lessonId);
          setLessonDetail(content);
        } catch {
          // ignore — detail panel will show stale data
        }
      }
    } catch (err: any) {
      setError(`Scrape failed: ${err.message}`);
    } finally {
      setScrapingLesson(null);
    }
  };

  const handleScrapeCourse = async (courseId: string) => {
    setScrapingCourse(courseId);
    setError(null);
    try {
      const { jobId } = await api.scrapeAllLessonsInCourse(courseId);
      setScrapeJobId(jobId);
      // Polling useEffect will handle progress and completion
    } catch (err: any) {
      setError(`Course scrape failed: ${err.message}`);
      setScrapingCourse(null);
    }
  };

  const handleSyncCourse = async (courseId: string) => {
    setSyncingCourse(courseId);
    setError(null);
    try {
      const result = await api.syncCourseForNewLessons(courseId);
      if (result.newLessons > 0) {
        showSuccess(`Found ${result.newLessons} new lesson${result.newLessons > 1 ? 's' : ''}, scraped ${result.scrapedLessons}`);
        // Refresh lessons and courses
        const data = await api.getLessons(courseId);
        setLessons(prev => ({ ...prev, [courseId]: data }));
      } else {
        showSuccess('No new lessons found — course is up to date');
      }
    } catch (err: any) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setSyncingCourse(null);
    }
  };

  const handleViewLesson = async (lessonId: string) => {
    if (selectedLessonId === lessonId) {
      // Toggle off
      setSelectedLessonId(null);
      setLessonDetail(null);
      return;
    }

    setSelectedLessonId(lessonId);
    setLessonDetail(null);
    setDetailTab('text');
    setLoadingDetail(true);

    try {
      const content = await api.getScrapedContent(lessonId);
      setLessonDetail(content);
    } catch {
      // No scraped content available
      setLessonDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create course button */}
      <div className="flex justify-between items-center">
        <p className="text-gray-600">Manage your courses and lessons that will be searchable.</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCSVImportModal(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Upload className="w-5 h-5" />
            Import CSV
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Import from ClickFunnels
          </button>
          <button
            onClick={() => setShowCourseForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Course
          </button>
        </div>
      </div>

      {/* New course form */}
      {showCourseForm && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Course</h3>
          <form onSubmit={handleCreateCourse} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={newCourseTitle}
                onChange={(e) => setNewCourseTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Course title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <textarea
                value={newCourseDesc}
                onChange={(e) => setNewCourseDesc(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Course description"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={creatingCourse}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingCourse ? 'Creating...' : 'Create Course'}
              </button>
              <button
                type="button"
                onClick={() => setShowCourseForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
          <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
          <p className="text-green-700">{successMsg}</p>
        </div>
      )}

      {/* Courses list */}
      {courses.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No courses yet. Create your first course to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {courses.map((course) => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {/* Course header */}
              <div
                className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCourse(course.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedCourse === course.id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-gray-900">{course.title}</h3>
                    {course.description && (
                      <p className="text-sm text-gray-500">{course.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-sm text-gray-500">
                    {lessons[course.id]?.length || 0} lessons
                  </span>
                  {course.clickfunnels_connection_id && (
                    <button
                      onClick={() => handleSyncCourse(course.id)}
                      disabled={syncingCourse === course.id}
                      className="px-3 py-1.5 text-xs font-medium border border-green-300 text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${syncingCourse === course.id ? 'animate-spin' : ''}`} />
                      {syncingCourse === course.id ? 'Checking...' : 'Check New'}
                    </button>
                  )}
                  <button
                    onClick={() => handleScrapeCourse(course.id)}
                    disabled={scrapingCourse === course.id}
                    className="px-3 py-1.5 text-xs font-medium border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {scrapingCourse === course.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Globe className="w-3.5 h-3.5" />
                    )}
                    {scrapingCourse === course.id
                      ? scrapeJobStatus
                        ? `${scrapeJobStatus.completed}/${scrapeJobStatus.total}`
                        : 'Starting...'
                      : 'Scrape All'}
                  </button>
                  <button
                    onClick={() => handleDeleteCourse(course.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete course"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Lessons section */}
              {expandedCourse === course.id && (
                <div className="border-t bg-gray-50">
                  {/* Add lesson button */}
                  <div className="px-6 py-3 border-b">
                    {showLessonForm === course.id ? (
                      <form onSubmit={(e) => handleCreateLesson(e, course.id)} className="space-y-3">
                        <input
                          type="text"
                          value={newLessonTitle}
                          onChange={(e) => setNewLessonTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Lesson title"
                          required
                        />
                        <input
                          type="text"
                          value={newLessonDesc}
                          onChange={(e) => setNewLessonDesc(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Description (optional)"
                        />
                        <input
                          type="url"
                          value={newLessonUrl}
                          onChange={(e) => setNewLessonUrl(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="https://example.com/lesson"
                          required
                        />
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={creatingLesson}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            {creatingLesson ? 'Adding...' : 'Add Lesson'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowLessonForm(null)}
                            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setShowLessonForm(course.id)}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Lesson
                      </button>
                    )}
                  </div>

                  {/* Lessons list */}
                  {lessons[course.id]?.length === 0 ? (
                    <div className="px-6 py-4 text-sm text-gray-500">
                      No lessons in this course yet.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {lessons[course.id]?.map((lesson) => (
                        <div key={lesson.id}>
                          <div className="px-6 py-3 flex items-center justify-between hover:bg-gray-100">
                            <div
                              className="flex items-center gap-3 flex-1 cursor-pointer"
                              onClick={() => lesson.content && handleViewLesson(lesson.id)}
                            >
                              <FileText className={`w-4 h-4 ${selectedLessonId === lesson.id ? 'text-blue-600' : 'text-gray-400'}`} />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{lesson.title}</p>
                                <div className="flex items-center gap-2">
                                  {lesson.description && (
                                    <p className="text-xs text-gray-500">{lesson.description}</p>
                                  )}
                                  {scrapeJobStatus?.lessonStatuses?.[lesson.id] === 'scraping' ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                      scraping
                                    </span>
                                  ) : scrapeJobStatus?.lessonStatuses?.[lesson.id] === 'done' ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                      <Check className="w-3 h-3" />
                                      done
                                    </span>
                                  ) : scrapeJobStatus?.lessonStatuses?.[lesson.id] === 'error' ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                      <AlertCircle className="w-3 h-3" />
                                      failed
                                    </span>
                                  ) : scrapeJobStatus?.lessonStatuses?.[lesson.id] === 'pending' ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                      <Clock className="w-3 h-3" />
                                      queued
                                    </span>
                                  ) : lesson.content ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                      <Check className="w-3 h-3" />
                                      scraped
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                      <Clock className="w-3 h-3" />
                                      not scraped
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {lesson.content && (
                                <button
                                  onClick={() => handleViewLesson(lesson.id)}
                                  className={`px-2.5 py-1 text-xs font-medium rounded-lg flex items-center gap-1 ${
                                    selectedLessonId === lesson.id
                                      ? 'border border-blue-500 text-blue-700 bg-blue-100'
                                      : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                                  }`}
                                >
                                  <Eye className="w-3 h-3" />
                                  {selectedLessonId === lesson.id ? 'Hide' : 'View'}
                                </button>
                              )}
                              <button
                                onClick={() => handleScrapeLesson(lesson.id, course.id)}
                                disabled={scrapingLesson === lesson.id || scrapingCourse === course.id}
                                className={`px-2.5 py-1 text-xs font-medium rounded-lg flex items-center gap-1 disabled:opacity-50 ${
                                  lesson.content
                                    ? 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                                    : 'border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                                }`}
                              >
                                {scrapingLesson === lesson.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Globe className="w-3 h-3" />
                                )}
                                {scrapingLesson === lesson.id ? 'Scraping...' : lesson.content ? 'Re-scrape' : 'Scrape'}
                              </button>
                              <a
                                href={lesson.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Open URL"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                              <button
                                onClick={() => handleDeleteLesson(lesson.id, course.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Lesson Detail Panel */}
                          {selectedLessonId === lesson.id && (
                            <div className="px-6 py-4 bg-white border-t border-gray-200">
                              {loadingDetail ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  Loading scraped content...
                                </div>
                              ) : !lessonDetail ? (
                                <div className="text-sm text-gray-500 py-4">
                                  No scraped content available. Click "Scrape" to fetch lesson content.
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {/* Metadata row */}
                                  <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                      {lessonDetail.platform}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {lessonDetail.wordCount.toLocaleString()} words
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {lessonDetail.videosDetected} video{lessonDetail.videosDetected !== 1 ? 's' : ''} detected
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      Scraped {new Date(lessonDetail.scrapedAt).toLocaleString()}
                                    </span>
                                    <a
                                      href={api.getScrapedMarkdownUrl(lesson.id)}
                                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                                      title="Download Markdown"
                                    >
                                      <Download className="w-3 h-3" />
                                      Download .md
                                    </a>
                                  </div>

                                  {/* Tab switcher */}
                                  <div className="flex border-b border-gray-200">
                                    {(['text', 'markdown', 'html'] as const).map((tab) => (
                                      <button
                                        key={tab}
                                        onClick={() => setDetailTab(tab)}
                                        className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                                          detailTab === tab
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                      >
                                        {tab === 'text' ? 'Plain Text' : tab === 'markdown' ? 'Markdown' : 'Raw HTML'}
                                      </button>
                                    ))}
                                  </div>

                                  {/* Content preview */}
                                  <div className="relative">
                                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs leading-relaxed overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                                      {detailTab === 'text' && lessonDetail.text}
                                      {detailTab === 'markdown' && lessonDetail.markdown}
                                      {detailTab === 'html' && lessonDetail.html}
                                    </pre>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ClickFunnels Import Modal */}
      <ClickFunnelsCourseImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={loadCourses}
      />

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showCSVImportModal}
        onClose={() => setShowCSVImportModal(false)}
        onImportComplete={loadCourses}
        courses={courses}
      />
    </div>
  );
}
